import { fabric } from "fabric";
import { v4 } from "uuid";
import store from "../../redux/store";
import { CanvasHandler } from "../../handlers";
import { FabricEvent, FabricObject,FabricGroup } from "../../types/FabricCustomType";
import { IObjectDesingner } from "../../types/HandlerObjectType";
import { RowProperties } from "./properties";
import { iconCheck } from "../../constants/Icons";
import { SeatShapeFactory, TextFactory } from "./factories";
import { SeatFactory } from "./factories/seat.factory";
import { ObjectSuperTypesEnum, ObjectTypesEnum } from "../../enums";
import { CURVE_MODE_RELATIVE } from "./helpers/RowSeatLayoutHelper";

class MultipleRows extends RowProperties implements IObjectDesingner {
  private defaultSeatColor: string;
  private rowSeatSpacing: number;
  private rowSeatSize: number;
  private rowSpacing: number;

  constructor(canvasHandler: CanvasHandler) {
    super(canvasHandler);
    this.rowSeatSpacing = 0;
  }

  public onInit() {
    this.canvasHandler.interactionHandler.drawing(ObjectSuperTypesEnum.Row, "crosshair");
    this.canvasHandler.activeShape = null;
    this.defaultSeatColor = "#000000";
  }

  onRotated(event: FabricEvent) {
    return;
  }

  onMouseDblClick(event: FabricEvent<Event>) {
    return;
  }

  public onMousedown(event: FabricEvent<MouseEvent>) {
    const {
      properties: { rowSeatSpacing, rowSeatSize, rowSpacing },
    } = store.getState();

    this.rowSeatSpacing = rowSeatSpacing;
    this.rowSeatSize = rowSeatSize;
    this.rowSpacing = rowSpacing;


    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);

    this.createPreviewGroup();
    this.canvasHandler.isDown = true;
    const seat = SeatShapeFactory.createSeatShape({
      left: x,
      top: y,
      rowSeatSize: this.rowSeatSize,
      defaultSeatColor: this.defaultSeatColor,
      editable: this.canvasHandler.editable,
      isPreview: true,
      id: v4(),
      container: this.canvasHandler.container.id,
      objectOption: this.canvasHandler.objectOption,
    });
    this.addSeatToPreviewGroup(seat);
  }

  public onMousemove(event: FabricEvent<MouseEvent>) {
    if (!this.canvasHandler.isDown || !this.canvasHandler.previewGroup) return;
    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    this.updatePreviewGrid(x, y);
  }

  public onMouseup(event: FabricEvent<MouseEvent>) {
    if (!this.canvasHandler.previewGroup) return;

    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    const canvasWidth = this.canvasHandler.canvas.getWidth();
    const canvasHeight = this.canvasHandler.canvas.getHeight();

    const dx = x - this.canvasHandler.originPointer.x;
    const dy = y - this.canvasHandler.originPointer.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance < 3 || x < 1 || y < 1 || x > canvasWidth || y > canvasHeight) {
      this.canvasHandler.isDown = false;
      this.canvasHandler.canvas.remove(this.canvasHandler.previewGroup);
      this.canvasHandler.previewGroup = {} as FabricGroup;
      return;
    }

    this.generateSeatGrid(x, y);
  }

  private createPreviewGroup() {
    const { editable } = this.canvasHandler;
    const id = v4();

    this.canvasHandler.previewGroup = new fabric.Group([], {
      left: this.canvasHandler.originPointer.x,
      top: this.canvasHandler.originPointer.y,
      originX: "left",
      originY: "top",
      hasControls: editable,
      hasBorders: editable,
      selectable: editable,
      type: ObjectTypesEnum.Group,
    }) as FabricObject<fabric.Group>;

    this.canvasHandler.previewGroup.set({
      id,
      superType: ObjectSuperTypesEnum.Row,
    });

    this.canvasHandler.canvas.add(this.canvasHandler.previewGroup);
  }

  private updatePreviewGrid(x: number, y: number) {
    const origin = { ...this.canvasHandler.originPointer };
    const group = this.canvasHandler.previewGroup;
    if (!group) return;

    group._objects.length = 0; // limpiar

    const width = Math.abs(x - origin.x);
    const height = Math.abs(y - origin.y);
    
    const colSpacing = this.rowSeatSize + this.rowSeatSpacing * 2;
    const rowSpacing = SeatShapeFactory.getOuterDiameter(this.rowSeatSize) + this.rowSpacing;

    const cols = Math.max(1, Math.floor(width / colSpacing));
    const rows = Math.max(1, Math.floor(height / rowSpacing));

    // El asiento de referencia (punto de clic) permanece fijo.
    // La cuadrícula crece hacia la dirección del cursor.
    const dirX = x < origin.x ? -1 : 1;
    const dirY = y < origin.y ? -1 : 1;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const seatX = origin.x + dirX * col * colSpacing + this.rowSeatSize / 2;
        const seatY = origin.y + dirY * row * rowSpacing + this.rowSeatSize / 2;
        const seat = SeatShapeFactory.createSeatShape({
          left: seatX,
          top: seatY,
          rowSeatSize: this.rowSeatSize,
          defaultSeatColor: this.defaultSeatColor,
          editable: this.canvasHandler.editable,
          isPreview: true,
          id: v4(),
          container: this.canvasHandler.container.id,
          objectOption: this.canvasHandler.objectOption,
        });
        group.addWithUpdate(seat);
      }
    }

    const editable = this.canvasHandler.editable;
    const gridLeft = Math.min(origin.x, origin.x + dirX * (cols - 1) * colSpacing);
    const gridTop = Math.min(origin.y, origin.y + dirY * (rows - 1) * rowSpacing);
    const gridWidth = (cols - 1) * colSpacing + this.rowSeatSize;
    const text = TextFactory.create({
          left: gridLeft + gridWidth / 2,
          top: gridTop - 20,
          fontSize: 13,
          editable,
          text: `${rows} filas x ${cols} columnas`
        });
    group.addWithUpdate(text);

    this.canvasHandler.canvas.renderAll();
  }

  private async generateSeatGrid(x: number, y: number) {
    const origin = { ...this.canvasHandler.originPointer };

    const width = Math.abs(x - origin.x);
    const height = Math.abs(y - origin.y);
    const colSpacing = this.rowSeatSize + this.rowSeatSpacing * 2;
    const rowSpacing = SeatShapeFactory.getOuterDiameter(this.rowSeatSize) + this.rowSpacing;

    const cols = Math.max(1, Math.floor(width / colSpacing) + 1);
    const rows = Math.max(1, Math.floor(height / rowSpacing));

    // El asiento de referencia (punto de clic) permanece fijo.
    // La cuadrícula crece hacia la dirección del cursor.
    const dirX = x < origin.x ? -1 : 1;
    const dirY = y < origin.y ? -1 : 1;

    if (this.canvasHandler.previewGroup) {
      this.canvasHandler.canvas.remove(this.canvasHandler.previewGroup);
    }

    const originalOrigin = { ...this.canvasHandler.originPointer };

    for (let r = 0; r < rows; r++) {
      const rowY = origin.y + dirY * r * rowSpacing + this.rowSeatSize / 2;
      const rowStartX = origin.x + this.rowSeatSize / 2;
      const rowEndX = origin.x + dirX * (cols - 1) * colSpacing + this.rowSeatSize / 2;

      this.canvasHandler.originPointer = { x: rowStartX, y: rowY };

      this.createRowGroup();

      const seatQuantity = await this.updateRow(rowEndX, rowY);

      this.canvasHandler.originPointer = { ...originalOrigin };

      this.canvasHandler.rowGroup?.set("seatsQuantity", seatQuantity);
      this.canvasHandler.rowGroup?.set("maxSeatsQuantity", null);
      this.canvasHandler.rowGroup?.set("rowSize", null);
      this.canvasHandler.rowGroup?.set("seatsSpacing", this.rowSeatSpacing);
      this.canvasHandler.rowGroup?.set("seatsCurve", 0);
      this.canvasHandler.rowGroup?.set("distanceBetweenX", 0);
      this.canvasHandler.rowGroup?.set("distanceBetweenY", 0);
  
      this.canvasHandler.rowGroup?.set("angleHelp", 0);
      // this.canvasHandler.group?.set('angle', angle);
      this.canvasHandler.rowGroup?.set("angleType", false);
      this.canvasHandler.rowGroup?.setCoords();
      this.canvasHandler.rowGroup?.addWithUpdate();
      this.canvasHandler.rowGroup?.set("hasControls", true);
      this.canvasHandler.rowGroup?.set("hoverCursor", "crosshair");
      // this.canvasHandler.rowGroup?.set("selectable", false);
      this.canvasHandler.rowGroup?.setControlsVisibility({
        tl: false, //top-left
        mt: false, // middle-top
        tr: false, //top-right
        ml: false, //middle-left
        mr: false, //middle-right
        bl: false, // bottom-left
        mb: false, //middle-bottom
        br: false, //bottom-right
        mtr: true,
      });
        if(this.canvasHandler.activeZone){
          this.canvasHandler.rowGroup?.set("zoneID", this.canvasHandler.activeZone.item(0).id)
          this.canvasHandler.rowGroup?.set({
            section:{
              label: this.canvasHandler.activeZone.section.label,
              displayedLabel: this.canvasHandler.activeZone.section.displayedLabel
            }
          })
        }
        // this.canvasHandler.canvas.renderAll();
        if (!this.canvasHandler.transactionHandler.active){
          this.canvasHandler.transactionHandler.save("add");
        }
        this.canvasHandler.onAdd(this.canvasHandler.rowGroup);
        this.canvasHandler.canvas.discardActiveObject();
        this.canvasHandler.rowGroup = {} as FabricGroup;
    }

    this.canvasHandler.isDown = false;
    this.canvasHandler.previewGroup = {} as FabricGroup;
    this.canvasHandler.canvas.renderAll();
  }

  private createRowGroup() {
    const { editable } = this.canvasHandler;
    const id = v4();
    this.canvasHandler.rowGroup = new fabric.Group(undefined, {
      left: this.canvasHandler.originPointer.x,
      top: this.canvasHandler.originPointer.y,
      originX: "left",
      originY: "top",
      hasControls: false,
      hasBorders: editable,
      selectable: editable,
      type: ObjectTypesEnum.Group,
      subTargetCheck: true,
      perPixelTargetFind: true,
    }) as FabricObject<fabric.Group>;

    this.canvasHandler.rowGroup.set({
      id: id,
      superType: ObjectSuperTypesEnum.Row,
      centerTransform: true,
      seatsQuantity: null,
      maxSeatsQuantity: null,
      rowSize: null,
      seatsSpacing: 0,
      seatsCurve: 0,
      curveMode: CURVE_MODE_RELATIVE,
      distanceBetweenX: 0,
      distanceBetweenY: 0,
      category: {
        key: 0,
        name: "",
      },
      seat: {
        key: 0,
        list: [] as Array<Number | String>,
        reverse: false,
        startsAt: 1,
      },
      section: {
        label: "",
        displayedLabel: "",
      },
      row: {
        enabled: false,
        label: "",
        displayedLabel: "",
        position: {
          left: false,
          right: false,
        },
      },
    });
    this.canvasHandler.canvas.add(this.canvasHandler.rowGroup);
  }

  private addSeatToPreviewGroup(obj: FabricObject) {
    this.canvasHandler.previewGroup?.addWithUpdate(obj);
    this.canvasHandler.canvas.renderAll();
  }

  private addSeatToRowGroup(obj: FabricObject<fabric.Group>) {
    this.canvasHandler.rowGroup?.addWithUpdate(obj);
    this.canvasHandler.canvas.renderAll();
  }

  private async updateRow(x: number, y: number): Promise<number> {
    const x1 = this.canvasHandler.originPointer.x;
    const y1 = this.canvasHandler.originPointer.y;

    const distanceX = x - x1;
    const distanceY = y - y1;
    const longDistance = Math.hypot(distanceX, distanceY);

    const seatSpacing = this.rowSeatSize + this.rowSeatSpacing * 2;
    let seatQuantity = Math.floor(longDistance / seatSpacing);
    seatQuantity = seatQuantity === 0 ? 1 : seatQuantity;

    const unitX = distanceX / longDistance;
    const unitY = distanceY / longDistance;

    for (let i = 0; i < seatQuantity; i++) {
      const seatX = x1 + unitX * seatSpacing * i;
      const seatY = y1 + unitY * seatSpacing * i;

      const seat = await SeatFactory.create({
        left: seatX,
        top: seatY,
        rowSeatSize: this.rowSeatSize,
        defaultSeatColor: this.defaultSeatColor,
        editable: this.canvasHandler.editable,
        isPreview: false,
        container: this.canvasHandler.container.id,
        objectOption: this.canvasHandler.objectOption,
        iconSvgString: iconCheck,
      });
      this.addSeatToRowGroup(seat);
    }

    this.canvasHandler.canvas.renderAll();

    return seatQuantity;
  }
}

export default MultipleRows;
